import { useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Hero3D } from "@/components/Hero3D";
import { TiltCard } from "@/components/TiltCard";
import { BUSINESS, IMAGES } from "@/lib/site";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const CATEGORIES = [
  { img: IMAGES.mobile, alt: "Latest smartphones on display", tag: "Mobiles & Accessories", title: "Mobile Phones & Gadgets",
    text: "All leading smartphone brands, fast adapters, noise-canceling earbuds, and power banks.", badge: "Factory Sealed", testid: "category-mobiles" },
  { img: IMAGES.appliances, alt: "Home appliances in store", tag: "Home Appliances", title: "Home Appliances",
    text: "Refrigerators, washing machines, Smart TVs with crisp displays, and air coolers.", badge: "GST Invoice", testid: "category-appliances" },
  { img: IMAGES.kitchen, alt: "Kitchen appliances on counter", tag: "Kitchen Gadgets", title: "Kitchen Appliances",
    text: "Mixer grinders, induction cooktops, microwave ovens, water purifiers, and electric kettles.", badge: "Brand Warranty", testid: "category-kitchen" },
  { img: IMAGES.electronics, alt: "General electronics and TVs", tag: "All Electronics", title: "General Electronics",
    text: "Soundbars, inverters, stabilizers, cables, fans, and office electronic equipment.", badge: "Bulk Available", testid: "category-electronics" },
];

const ADVANTAGES = [
  { icon: "₹", title: "Best Prices in the Area",
    text: "Direct retail relationships mean you get the lowest price points without inflated markups or hidden fees.", testid: "advantage-prices" },
  { icon: "✓", title: "Proper Warranty & GST Bill",
    text: "Never worry about fake warranty cards. Every purchase is paired with an official GST tax invoice honored by brand service networks.", testid: "advantage-warranty" },
  { icon: "★", title: "Friendly After-Sales Support",
    text: "We are accessible by phone, WhatsApp, and in-store. If you need setup help or service assistance, we stand by you.", testid: "advantage-support" },
];

const FAQS = [
  { q: "How can I place an order or ask for product prices?",
    a: "Call or message us on WhatsApp at 9076585965, or submit the enquiry form below. We provide current pricing and stock details immediately." },
  { q: "Are all products backed by brand warranty?",
    a: "Yes. Every device is 100% genuine with sealed packaging, GST bill, and official manufacturer warranty coverage." },
  { q: "Can I come to the store to see the products?",
    a: "Yes, our store is located in the local area for direct customer walk-ins and live product demonstrations." },
];

export default function Home() {
  useEffect(() => { document.title = "SUDHEER ELECTRONIC | Electronics Shop — Mobiles, Home & Kitchen Appliances"; }, []);
  return (
    <div className="bg-[#f4f7fa] text-slate-900 antialiased">
      <Navbar />

      {/* HERO */}
      <section id="hero" className="py-16 sm:py-24 bg-white border-b border-slate-200" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div {...fadeUp} className="lg:col-span-7">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-950 tracking-tight leading-[1.1] mb-6">
                Your Local Electronics Counter.
              </h1>
              <p className="text-base sm:text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                Shop all kinds of genuine electronics, smartphones, and household kitchen appliances with authentic
                brand warranty, GST invoice, and best prices in the local area.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 text-sm font-bold text-white bg-blue-700 hover:bg-blue-600 rounded-lg shadow-md transition-colors">
                  <MessageCircle size={18} /> Chat on WhatsApp for Prices
                </a>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-4 pt-8 border-t border-slate-100 text-xs">
                <div data-testid="hero-trust-price"><span className="font-bold text-slate-950 block">Lowest Rate</span><span className="text-slate-500">Local price match</span></div>
                <div data-testid="hero-trust-bill"><span className="font-bold text-slate-950 block">Official Bill</span><span className="text-slate-500">Full GST invoice</span></div>
                <div data-testid="hero-trust-support"><span className="font-bold text-slate-950 block">Direct Help</span><span className="text-slate-500">After-sales support</span></div>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.15 }} className="lg:col-span-5">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-slate-300 shadow-xl bg-slate-950" data-testid="hero-3d-showcase">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.35),transparent_55%),radial-gradient(circle_at_80%_80%,rgba(14,165,233,0.25),transparent_50%)]" />
                <Hero3D />
                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white pointer-events-none">
                  <div className="flex justify-between items-center text-xs font-mono text-blue-200">
                    <span>SUDHEER ELECTRONIC</span><span>3D SHOWCASE</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Authentic Appliances &amp; Mobiles</h3>
                    <p className="text-xs text-slate-300 leading-relaxed">Move your cursor to explore. Direct from authorized channels with full factory warranty.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="showcase" className="py-20 bg-[#f4f7fa]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <h2 className="text-3xl font-extrabold text-slate-950 tracking-tight">Comprehensive Electronics Range</h2>
            <p className="text-slate-600 mt-2 text-sm">Browse items ready for instant in-store inspection or fast local delivery.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((c, i) => (
              <motion.div key={c.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="[perspective:1000px]">
              <TiltCard data-testid={c.testid}
                className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between hover:shadow-xl transition-shadow h-full">
                <div>
                  <div className="relative aspect-[4/3] rounded-lg overflow-hidden mb-5 [transform:translateZ(30px)]">
                    <img src={c.img} alt={c.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    <span className="absolute bottom-2 left-2 bg-white/95 text-slate-800 text-[11px] font-bold px-2 py-1 rounded">{c.tag}</span>
                  </div>
                  <h3 className="text-base font-bold text-slate-950 mb-2">{c.title}</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">{c.text}</p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] font-bold text-blue-700">{c.badge}</span>
                  <a href="/contact" data-testid={`${c.testid}-enquire-link`} className="text-xs font-bold text-slate-900 hover:text-blue-700 transition-colors">Enquire &rarr;</a>
                </div>
              </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section id="features" className="py-20 bg-white" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-extrabold text-slate-950 tracking-tight">Why Buy from SUDHEER ELECTRONIC?</h2>
            <p className="text-slate-600 mt-2 text-sm">Clear advantages that keep local families, students, and businesses coming back.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ADVANTAGES.map((a, i) => (
              <motion.div key={a.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} data-testid={a.testid}
                className="p-8 rounded-2xl bg-blue-50/50 border border-blue-100">
                <div className="w-12 h-12 bg-blue-700 text-white rounded-xl flex items-center justify-center font-bold text-lg mb-6">{a.icon}</div>
                <h3 className="text-lg font-bold text-slate-950 mb-2">{a.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{a.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BULK BANNER */}
      <section id="services" className="py-16 bg-slate-900 text-white" data-testid="bulk-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Supplying Bulk Electronics to Offices &amp; Shops</h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Need multiple appliances, electronics packages, or staff mobile handsets? Get discounted commercial pricing and full input GST billing.
              </p>
            </div>
            <div className="flex-shrink-0">
              <a href={BUSINESS.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="bulk-quote-button"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-colors">
                Get Bulk Quotation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-[#f4f7fa]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeUp} className="text-3xl font-extrabold text-slate-950 tracking-tight text-center mb-12">
            Frequently Asked Questions
          </motion.h2>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <motion.div key={f.q} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} data-testid={`faq-item-${i + 1}`}
                className="p-6 bg-white rounded-xl border border-slate-200">
                <h3 className="text-sm font-bold text-slate-950 mb-2">{f.q}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-16 bg-white border-b border-slate-200" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div {...fadeUp} className="lg:col-span-5">
              <h2 className="text-2xl font-bold text-slate-950 mb-3">Store Location &amp; Visit Info</h2>
              <p className="text-xs text-slate-600 leading-relaxed mb-4">
                Customers come directly to our shop for personal assistance, warranty bills, and immediate pickup.
              </p>
              <div className="text-xs text-slate-800 space-y-1">
                <p><span className="font-bold">Service Area:</span> Local area &middot; customers come to the shop</p>
                <p><span className="font-bold">Phone / WhatsApp:</span> {BUSINESS.phoneDisplay}</p>
                <p><span className="font-bold">Email:</span> {BUSINESS.email}</p>
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-7">
              <div className="relative aspect-[16/9] rounded-xl overflow-hidden border border-slate-300">
                <img src={IMAGES.map} alt="Local area map" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="text-xs font-bold text-slate-700 uppercase bg-white/80 self-start px-2 py-1 rounded">Local Store Location</div>
                  <div className="bg-white/95 p-3 rounded-lg shadow text-xs font-semibold text-slate-950 max-w-xs">
                    SUDHEER ELECTRONIC Showroom Counter
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 bg-slate-900 text-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <motion.div {...fadeUp} className="lg:col-span-5">
              <h2 className="text-3xl font-extrabold tracking-tight mb-4">Connect with SUDHEER ELECTRONIC</h2>
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed mb-6">
                Get quick price quotes, check product stock, or enquire about bulk office purchases.
              </p>
              <div className="space-y-4 mb-6">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10" data-testid="contact-phone-card">
                  <span className="text-xs font-bold text-blue-400 block uppercase">Phone &amp; WhatsApp</span>
                  <span className="text-lg font-bold text-white block mt-1">{BUSINESS.phoneDisplay}</span>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10" data-testid="contact-email-card">
                  <span className="text-xs font-bold text-blue-400 block uppercase">Official Email</span>
                  <span className="text-xs text-white block mt-1 break-words">{BUSINESS.email}</span>
                </div>
              </div>
              <div className="flex gap-3">
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded transition-colors">
                  <MessageCircle size={16} /> WhatsApp Chat
                </a>
                <a href={`tel:${BUSINESS.phoneIntl}`} data-testid="contact-call-button"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded transition-colors">
                  <Phone size={16} /> Call Direct
                </a>
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-7">
              <EnquiryForm />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
